<template>
  <section class="home-onboarding">
    <h1 class="font-weight-bold">Welcome to Floor Plan Mapper Online</h1>

    <b-card v-if="loading" class="mt-3">
      <span class="text-muted">Loading...</span>
    </b-card>

    <!--
      Not one of the steps is theirs to take: in an empty workspace a plain user has nothing
      to do but wait, and a checklist of things they cannot do would only read as a demand.
      See docs/home-screen.md, S0.
    -->
    <p v-else-if="!steps.length" class="text-muted">Your administrator is still setting up this workspace.</p>

    <template v-else>
      <p class="lead text-muted">Nothing is on the map yet. These steps put it there.</p>

      <div class="d-flex flex-wrap align-items-center mb-4" style="gap: 0.5rem 1.5rem;">
        <b-btn v-if="nextStep" variant="primary btn-round" :to="nextStep.to">{{ nextStep.cta }}</b-btn>
        <a href="/guide.pdf" target="_blank" rel="noopener">Setup and Administration Guide</a>
        <span v-if="!loadFailed" class="text-muted small ml-auto">{{ doneCount }} of {{ steps.length }} done</span>
      </div>

      <!-- the checklist still names the steps in order; only the ticks are missing -->
      <p v-if="loadFailed" class="text-muted small">Could not check how far the setup has got.</p>

      <b-card no-body>
        <ul class="steps list-unstyled mb-0">
          <li v-for="step in steps" :key="step.key" class="step" :class="{ 'is-done': step.done }">
            <span class="step__mark"><i v-if="step.done" class="ion ion-md-checkmark" /></span>
            <div class="step__body">
              <router-link :to="step.to" class="step__label">{{ step.label }}</router-link>
              <div class="text-muted small">
                {{ step.hint }}
                <router-link v-if="step.also" :to="step.also.to" class="ml-1">{{ step.also.label }}</router-link>
              </div>
            </div>
          </li>
        </ul>
      </b-card>
    </template>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import * as PN from '@/utils/PermissionNames'

export default {
  name: 'HomeOnboarding',
  data: () => ({
    // one flag per step, as /home/onboarding answers them
    progress: {},
    loading: true,
    loadFailed: false
  }),
  computed: {
    ...mapGetters('rbac', ['can']),
    ...mapGetters('floor', ['floors']),
    ...mapState('auth', ['user']),
    /**
     * The floor the plan step should open.
     *
     * The first one still without a drawing, because that is the one the step is about; any
     * floor otherwise, so the link still lands somewhere useful once they all have plans.
     */
    planFloorId() {
      const floor = this.floors.find((row) => !row.map_updated_at) || this.floors[0]
      return floor ? floor.id : null
    },
    /**
     * Every step of the setup, in the order it has to happen, with the permission that makes
     * it somebody's to take. A step nobody here can take is not shown - see the template.
     */
    allSteps() {
      return [
        {
          key: 'building',
          label: 'Add your building',
          cta: 'Add a building',
          hint: 'Be sure to set the relevant time zone for the building. Desk Booking relies on accurate time zones.',
          to: { name: 'floors' },
          permission: PN.BUILDING_CREATE
        },
        {
          key: 'floor',
          label: 'Add a floor',
          cta: 'Add a floor',
          hint: 'Designate floor names for each floor in the building.',
          to: { name: 'floors' },
          permission: PN.FLOOR_CREATE
        },
        {
          key: 'floor_plan',
          label: 'Upload a floor plan',
          cta: 'Upload a floor plan',
          hint: 'Upload a floor plan. PDF is preferred, but any image format will do.',
          to: this.planFloorId ? { name: 'floor-edit', params: { floorId: this.planFloorId } } : { name: 'floors' },
          permission: PN.FLOOR_UPDATE
        },
        {
          key: 'desks',
          label: 'Place desks on the map',
          cta: 'Add desks',
          hint:
            'Note: Be sure to number your desks as you add them. The file import and Office 365 integrations can use this desk number to auto seat employees.',
          to: { name: 'map-home' },
          permission: PN.DESK_CREATE,
          // without booking there is nothing to put a desk on the map for
          enabled: Boolean(this.user && this.user.booking_enabled)
        },
        {
          key: 'employees',
          label: 'Add your people',
          cta: 'Add employees',
          hint:
            "Note: It's super helpful if your employee directory file has fields like building, floor, and desk number. This enables Floor Plan Mapper to seat employees on desks automatically.",
          to: { name: 'employees' },
          also: { label: 'Import from a file', to: { name: 'employees-import' } },
          permission: PN.EMPLOYEE_CREATE
        },
        {
          key: 'users',
          label: 'Invite your team',
          cta: 'Invite your team',
          hint: 'Everyone who signs in needs an account of their own.',
          to: { name: 'users' },
          permission: PN.USER_CRUD
        }
      ]
    },
    steps() {
      return this.allSteps
        .filter((step) => step.enabled !== false && this.can(step.permission))
        .map((step) => ({ ...step, done: Boolean(this.progress[step.key]) }))
    },
    doneCount() {
      return this.steps.filter((step) => step.done).length
    },
    // where the one primary button goes: the first step still waiting on them
    nextStep() {
      return this.steps.find((step) => !step.done) || null
    }
  },
  methods: {
    ...mapActions('home', ['onboardingLoad']),
    async load() {
      try {
        const response = await this.onboardingLoad()
        this.progress = response.data
        this.loadFailed = false
      } catch (e) {
        // Api already toasts the failure; the checklist falls back to naming the steps, which
        // is most of its job - the ticks are the part that needs the server
        this.progress = {}
        this.loadFailed = true
      } finally {
        this.loading = false
      }
    }
  },
  created() {
    this.load()
  }
}
</script>

<style scoped lang="scss">
.step {
  align-items: flex-start;
  border-top: 1px solid #eee;
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;

  &:first-child {
    border-top: 0;
  }

  // a ring that fills in when the step is done - drawn rather than iconised, so the two
  // states are the same shape and the list does not jump as they land
  &__mark {
    align-items: center;
    border: 2px solid #dfe1e3;
    border-radius: 50%;
    color: #fff;
    display: flex;
    flex: 0 0 auto;
    height: 1.4rem;
    justify-content: center;
    margin-top: 0.1rem;
    width: 1.4rem;
  }

  &.is-done &__mark {
    background: #02bc77;
    border-color: #02bc77;
  }

  &__label {
    font-weight: 600;
  }

  &.is-done &__label {
    color: #4e5155;
    text-decoration: line-through;
  }
}
</style>
